import React, { useEffect, useState } from 'react';
import './Contact.css';
import {
	TextField,
	Typography,
	IconButton,
	Dialog,
	DialogContent,
	DialogTitle,
	Container,
	Box,
	Button,
	DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import EditIcon from '@mui/icons-material/Edit';
import { ToastContainer, toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridDeleteIcon, GridValueGetter, GridColDef, GridToolbar } from '@mui/x-data-grid';
import CreateContacts from './CreateContact';
import { deleteContacts, getAllContacts, updateContacts } from '../../../services/api/Contacts';
import { StyledCrossIconButton, StyledDataGrid } from '../../component/ThemeComponent';

interface ContactCardProps {
	_id: string;
	name: string;
	email: string;
	mobile: string;
	message: string;
	status: number;
	followupStatus: string;
	createdAt?: string;
	updatedAt?: string;
}

interface GridRowParams {
	row: ContactCardProps;
}

const Contact = () => {
	const [contacts, setContacts] = useState<ContactCardProps[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [openDialog, setOpenDialog] = useState(false);
	const [currentProduct, setCurrentProduct] = useState<ContactCardProps | null>(null);
	const [visibleEnquiry, setVisibleEnquiry] = useState(false);

	// const handleAddCategory = () => {
	// 	setCurrentProduct(null);
	// 	setOpenDialog(true);
	// 	setVisibleEnquiry(true);
	// };

	const handleEditProduct = (product: ContactCardProps) => {
		setCurrentProduct(product);
		setOpenDialog(true);
		setVisibleEnquiry(true);
	};

	const handleViewProduct = (product: ContactCardProps) => {
		setCurrentProduct(product);
		setOpenDialog(true);
		setVisibleEnquiry(false);
	};

	const handleDeleteProduct = async (id: string) => {
		const response = await deleteContacts(id);
		if (response.success) {
			const updatedContacts = contacts.filter((contact) => contact._id !== id);
			setContacts(updatedContacts);
			toast.success('Contact deleted successfully!');
		} else {
			toast.error('Failed to delete contact.');
		}
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setCurrentProduct(null);
	};

	const handleFormSubmit = async (formData: ContactCardProps) => {
		try {
			const response = await updateContacts({ ...formData }, formData._id);
			if (response.success) {
				const updatedContacts = contacts.map((contact) =>
					contact._id === formData._id ? response.data : contact
				);
				setContacts(updatedContacts);
				setOpenDialog(false);
				toast.success('Contact updated successfully!');
			} else {
				toast.error('Failed to update contact.');
			}
		} catch (error) {
			toast.error('Error updating contact.');
		}
	};

	useEffect(() => {
		fetchData();
	}, []);

	const fetchData = async () => {
		const response = await getAllContacts();
		if (response.success) {
			setContacts(response?.data?.inquiries);
		}
	};

	const handleSaveContact = () => {
		const form = document.getElementById('create-contact-form') as HTMLFormElement;
		if (form) {
			form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
		}
	};

	const filteredContacts = contacts.filter((contact) =>
		contact.name.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const createdAtDate: GridValueGetter<(typeof contacts)[number], unknown> = (value, row) =>
		`${
			new Date(row.createdAt ?? '')
				.toLocaleString('en-GB', {
					day: '2-digit',
					month: 'short',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
					hour12: true,
				})
				.replace(/,/g, '')
				.replace(/(\d+)\/(\d+)\/(\d+),? (\d+:\d+) (AM|PM)/, '$1-$2-$3 $4 $5') || 'N/A'
		}`;

	const updatedAtDate: GridValueGetter<(typeof contacts)[number], unknown> = (value, row) =>
		`${
			new Date(row.updatedAt ?? '')
				.toLocaleString('en-GB', {
					day: '2-digit',
					month: 'short',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
					hour12: true,
				})
				.replace(/,/g, '')
				.replace(/(\d+)\/(\d+)\/(\d+),? (\d+:\d+) (AM|PM)/, '$1-$2-$3 $4 $5') || 'N/A'
		}`;

	const columns: GridColDef[] = [
		{ field: 'name', headerName: 'Full Name', width: 150, type: 'string' },
		{ field: 'email', headerName: 'Email', width: 200, type: 'string' },
		{ field: 'mobile', headerName: 'Mobile No', width: 130, type: 'string' },
		{
			field: 'createdAt',
			headerName: 'Lead Date',
			width: 180,
			type: 'string',
			valueGetter: createdAtDate,
		},
		{
			field: 'updatedAt',
			headerName: 'Update Date',
			width: 180,
			type: 'string',
			valueGetter: updatedAtDate,
		},
		{ field: 'followupStatus', headerName: 'Follow Up Status', width: 150, type: 'string' },
		{
			field: 'actions',
			headerName: 'Actions',
			width: 150,
			type: 'actions',
			renderCell: (params: GridRowParams) => (
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<IconButton onClick={() => handleViewProduct(params.row)}>
						<VisibilityIcon />
					</IconButton>
					<IconButton onClick={() => handleEditProduct(params.row)}>
						<EditIcon />
					</IconButton>
					<IconButton onClick={() => handleDeleteProduct(params.row._id)}>
						<GridDeleteIcon />
					</IconButton>
				</div>
			),
		},
	];

	return (
		<Container>
			<div
				className="d-flex"
				style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
			>
				<div>
					<Typography
						gutterBottom
						style={{ fontFamily: 'Poppins', fontWeight: '700', fontSize: '24px' }}
					>
						Contacts
					</Typography>
					<span
						style={{
							fontFamily: 'Poppins',
							fontWeight: '400',
							fontSize: '14px',
							color: '#006D48',
						}}
					>
						Manage your contact list, add new contacts, and more.
					</span>
				</div>
				{/* <div>
					<Button variant="contained" color="primary" onClick={handleAddCategory}>
						Add Contact
					</Button>
				</div> */}
			</div>

			<Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
				<Grid size={{ xs: 12 }}>
					<TextField
						label="Search Contacts"
						variant="outlined"
						margin="none"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						size="small"
						fullWidth
						sx={{ marginTop: '10px' }}
					/>
				</Grid>
			</Grid>

			<Box sx={{ height: 600, width: 'auto' }}>
				<StyledDataGrid
					rows={filteredContacts}
					columns={columns}
					initialState={{
						pagination: {
							paginationModel: { pageSize: 10 },
						},
					}}
					slots={{ toolbar: GridToolbar }}
					pageSizeOptions={[10]}
					getRowId={(row) => row._id}
				/>
			</Box>

			<Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
				<DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<span>{visibleEnquiry ? 'Edit Contact' : 'View Contact'}</span>
					<StyledCrossIconButton onClick={handleCloseDialog}>
						<CloseIcon />
					</StyledCrossIconButton>
				</DialogTitle>
				<DialogContent>
					<CreateContacts
						product={currentProduct}
						onSubmit={handleFormSubmit}
						isDisabled={!visibleEnquiry}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog} color="primary">
						Cancel
					</Button>
					{visibleEnquiry && (
						<Button onClick={handleSaveContact} color="primary">
							Save
						</Button>
					)}
				</DialogActions>
			</Dialog>
			<ToastContainer />
		</Container>
	);
};

export default Contact;
